import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty, StatusBadge } from "../components/ui-helpers";
import { FamilyToggle } from "../components/FamilyToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, ArrowLeft, SealCheck, Stack, Printer, ClockCounterClockwise, Trash, FilePdf, FileDoc, Pencil, FloppyDisk, ArrowUUpLeft, BookOpen, ArrowsClockwise } from "@phosphor-icons/react";
import ApprovalWorkflow from "../components/ApprovalWorkflow";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import GlossaryText from "../components/GlossaryText";

const signerFor = (m) => ((m?.approver_title || "").trim() || ((m?.family || "").trim().toUpperCase() === "CIP" ? "CIP Senior Manager" : "Authorized Approver"));

const REVIEW = {
  overdue: { cls: "bg-red-50 text-red-700 border-red-300", label: "Review Overdue" },
  due_soon: { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Review Due Soon" },
  current: { cls: "bg-green-50 text-green-700 border-green-300", label: "Current" },
  never_approved: { cls: "bg-secondary text-gray-600 border-gray-300", label: "Never Approved" },
};
const SCOPES = [
  ["effective_now", "Effective now only"],
  ["effective_plus_15mo", "Effective + becoming effective within 15 months"],
  ["all", "All versions (including future)"],
];
const scopeLabel = (s) => (SCOPES.find((x) => x[0] === s) || SCOPES[0])[1];
function ReviewBadge({ state, days }) {
  const r = REVIEW[state] || REVIEW.never_approved;
  return <span className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase ${r.cls}`}>{r.label}{typeof days === "number" ? (days < 0 ? ` · ${Math.abs(days)}d ago` : ` · ${days}d`) : ""}</span>;
}

function FrontSection({ title, testid, id, children }) {
  return (
    <div id={id} className="px-5 py-4 border-b border-border scroll-mt-24" data-testid={testid}>
      <h3 className="font-display font-bold text-base mb-2">{title}</h3>
      {children}
    </div>
  );
}

const TODAY_ISO = new Date().toISOString().slice(0, 10);
const secId = (sid) => "sec-std_" + String(sid || "").replace(/[^A-Za-z0-9]/g, "_");

function Ed({ edit, value, onChange, rows = 2, className = "" }) {
  if (!edit) return null;
  return (
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
      className={`w-full border border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20 rounded-none px-2 py-1 text-sm ${className}`} />
  );
}

function StandardsSelector({ family, asOf, standardIds, onChange, testidPrefix = "std" }) {
  const [cands, setCands] = useState(null);
  const [loading, setLoading] = useState(false);
  const inited = useRef(false);
  useEffect(() => { inited.current = false; }, [family]);
  useEffect(() => {
    if (!family) return;
    setLoading(true);
    api.get("/policy-manuals/candidate-standards", { params: { family, as_of: asOf } })
      .then((r) => {
        setCands(r.data.standards);
        if (!inited.current && (!standardIds || standardIds.length === 0)) {
          onChange({ as_of_date: asOf, standard_ids: r.data.recommended_ids });
        }
        inited.current = true;
      })
      .catch(() => setCands([]))
      .finally(() => setLoading(false));
  }, [family, asOf]);

  const sel = new Set(standardIds || []);
  const recommended = (cands || []).filter((c) => c.recommended).map((c) => c.standard_id);
  const currentAndFuture = (cands || [])
    .filter((c) => (c.applicable || c.cfr_covered) && c.in_region && !c.retired_as_of && (c.enforceable_as_of || c.effective_date))
    .map((c) => c.standard_id);
  const allApplicableSelected = recommended.length > 0 && recommended.every((id) => sel.has(id));
  const allCurrentFutureSelected = currentAndFuture.length > 0 && currentAndFuture.every((id) => sel.has(id));
  const set = (ids) => onChange({ as_of_date: asOf, standard_ids: ids });
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); set([...n]); };

  return (
    <div data-testid={`${testidPrefix}-selector`}>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>Compile for enforcement as of</Label>
          <Input data-testid={`${testidPrefix}-asof`} type="date" value={asOf || TODAY_ISO}
            onChange={(e) => onChange({ as_of_date: e.target.value, standard_ids: standardIds })}
            className="rounded-none mt-1 w-44" />
        </div>
        <p className="text-[11px] text-muted-foreground flex-1 min-w-[200px] pb-1">
          Use a future date to plan ahead — includes standards that become enforceable by then and excludes any retired by then, so your policies are ready on day one.
        </p>
      </div>

      <div className="flex items-center gap-3 mt-3 mb-1 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
          <input data-testid={`${testidPrefix}-select-all-applicable`} type="checkbox" checked={allApplicableSelected}
            onChange={(e) => set(e.target.checked ? [...new Set([...(standardIds || []), ...recommended])] : (standardIds || []).filter((id) => !recommended.includes(id)))} />
          Select all applicable in my region ({recommended.length})
        </label>
        <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer text-amber-800">
          <input data-testid={`${testidPrefix}-select-current-future`} type="checkbox" checked={allCurrentFutureSelected}
            onChange={(e) => set(e.target.checked ? [...new Set([...(standardIds || []), ...currentAndFuture])] : (standardIds || []).filter((id) => !currentAndFuture.includes(id)))} />
          Include all current + future enforceable ({currentAndFuture.length})
        </label>
        <button type="button" onClick={() => set((cands || []).map((c) => c.standard_id))} className="text-[11px] font-semibold text-indigo-700 hover:underline">Select all listed</button>
        <button type="button" onClick={() => set([])} className="text-[11px] font-semibold text-muted-foreground hover:underline">Clear</button>
        <span className="text-[11px] text-muted-foreground ml-auto" data-testid={`${testidPrefix}-count`}>{sel.size} selected</span>
      </div>

      <div className="border border-border max-h-64 overflow-y-auto" data-testid={`${testidPrefix}-list`}>
        {loading || cands === null ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">Loading standards…</div>
        ) : cands.length === 0 ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">No {family} standards found.</div>
        ) : cands.map((c) => (
          <label key={c.standard_id} data-testid={`${testidPrefix}-row-${c.standard_id}`}
            className={`flex items-start gap-2 px-3 py-1.5 border-b border-border/60 cursor-pointer hover:bg-secondary/20 ${!c.recommended ? "opacity-80" : ""}`}>
            <input type="checkbox" data-testid={`${testidPrefix}-check-${c.standard_id}`} className="mt-1" checked={sel.has(c.standard_id)} onChange={() => toggle(c.standard_id)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-xs font-bold">{c.standard_id}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[280px]">{c.title}</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                {!c.applicable && <span className="text-[9px] uppercase font-bold border border-gray-300 bg-gray-50 text-gray-500 px-1">Not applicable</span>}
                {!c.in_region && <span className="text-[9px] uppercase font-bold border border-gray-300 bg-gray-50 text-gray-500 px-1">Out of region{c.region ? ` · ${c.region}` : ""}</span>}
                {!c.enforceable_as_of && c.effective_date && <span className="text-[9px] uppercase font-bold border border-amber-300 bg-amber-50 text-amber-700 px-1">Eff {c.effective_date}</span>}
                {c.retired_as_of && <span className="text-[9px] uppercase font-bold border border-red-300 bg-red-50 text-red-700 px-1">Retired</span>}
                {c.cfr_covered && <span className="text-[9px] uppercase font-bold border border-violet-300 bg-violet-50 text-violet-700 px-1">CFR</span>}
                {c.recommended && <span className="text-[9px] uppercase font-bold border border-emerald-300 bg-emerald-50 text-emerald-700 px-1">Recommended</span>}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}


export default function PolicyManuals() {
  const { isRole } = useAuth();
  const { isModuleHidden } = useModuleVisibility();
  const approvalsEnabled = !isModuleHidden("approvals");
  const canEdit = isRole("admin", "analyst");
  const canApprove = isRole("admin", "reviewer");
  const [manuals, setManuals] = useState([]);
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", family: "CIP", cip_senior_manager: "", approver_title: "", review_interval_months: 15, description: "", effective_scope: "effective_now", as_of_date: TODAY_ISO, standard_ids: [], include_future_transition_language: true });
  const [approveOpen, setApproveOpen] = useState(false);
  const [approval, setApproval] = useState({ approved_by: "", comment: "" });
  const [editStdOpen, setEditStdOpen] = useState(false);
  const [stdForm, setStdForm] = useState({ as_of_date: TODAY_ISO, standard_ids: [] });
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [sp] = useSearchParams();
  const family = sp.get("family") || "";

  const loadList = useCallback(() => api.get(`/policy-manuals${family ? `?family=${family}` : ""}`).then((r) => setManuals(r.data)), [family]);
  const loadDetail = useCallback((id) => api.get(`/policy-manuals/${id}`).then((r) => setDetail(r.data)), []);
  const loadVersions = useCallback((id) => api.get(`/policy-manuals/${id}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([])), []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selId) { loadDetail(selId); loadVersions(selId); setEditMode(false); } else { setDetail(null); } }, [selId, loadDetail, loadVersions]);

  // ---- draft editing ----
  const startEdit = () => { setDraft(structuredClone({ front_matter: detail.front_matter || {}, compiled_body: detail.compiled_body || [] })); setEditMode(true); };
  const cancelEdit = () => { setEditMode(false); setDraft(null); };
  const setFM = (k, v) => setDraft((d) => ({ ...d, front_matter: { ...d.front_matter, [k]: v } }));
  const setFMArr = (k, i, v) => setDraft((d) => { const arr = [...(d.front_matter[k] || [])]; arr[i] = v; return { ...d, front_matter: { ...d.front_matter, [k]: arr } }; });
  const setStdF = (si, k, v) => setDraft((d) => { const b = structuredClone(d.compiled_body); b[si][k] = v; return { ...d, compiled_body: b }; });
  const setReqF = (si, ri, k, v) => setDraft((d) => { const b = structuredClone(d.compiled_body); b[si].requirements[ri][k] = v; return { ...d, compiled_body: b }; });
  const setPartF = (si, ri, pi, v) => setDraft((d) => { const b = structuredClone(d.compiled_body); b[si].requirements[ri].parts[pi].statement = v; return { ...d, compiled_body: b }; });
  const saveDraft = async () => {
    setBusy("save-draft");
    try {
      const { data } = await api.put(`/policy-manuals/${selId}/draft`, { front_matter: draft.front_matter, compiled_body: draft.compiled_body });
      setDetail(data); setEditMode(false); setDraft(null); toast.success("Draft saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const saveVersion = async () => {
    const label = window.prompt("Name this version (e.g. 'Reviewed draft — Jan'):", "");
    if (label === null) return;
    try { await api.post(`/policy-manuals/${selId}/versions`, { label }); loadVersions(selId); toast.success("Version saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore "${v.label}"? Your current draft will be auto-saved as a version first.`)) return;
    try { const { data } = await api.post(`/policy-manuals/${selId}/versions/${v.id}/restore`); setDetail(data); loadVersions(selId); toast.success("Version restored"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteVersion = async (v) => {
    if (!window.confirm(`Delete version "${v.label}"?`)) return;
    try { await api.delete(`/policy-manuals/${selId}/versions/${v.id}`); loadVersions(selId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const scrollToSec = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });


  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      const { data } = await api.post("/policy-manuals", form);
      setCreating(false); setForm({ title: "", family: "CIP", cip_senior_manager: "", approver_title: "", review_interval_months: 15, description: "", effective_scope: "effective_now", as_of_date: TODAY_ISO, standard_ids: [], include_future_transition_language: true });
      await loadList(); setSelId(data.id); toast.success("Manual created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditStd = () => { setStdForm({ as_of_date: detail.as_of_date || TODAY_ISO, standard_ids: detail.standard_ids || [], include_future_transition_language: detail.include_future_transition_language !== false }); setEditStdOpen(true); };
  const saveStandards = async () => {
    try {
      const { data } = await api.put(`/policy-manuals/${selId}`, {
        title: detail.title, family: detail.family, description: detail.description || "",
        cip_senior_manager: detail.cip_senior_manager || "", approver_title: detail.approver_title || "",
        review_interval_months: detail.review_interval_months || 15,
        effective_scope: detail.effective_scope || "effective_now",
        as_of_date: stdForm.as_of_date, standard_ids: stdForm.standard_ids,
        include_future_transition_language: stdForm.include_future_transition_language !== false,
        policy_ids: detail.policy_ids || [],
      });
      setDetail(data); setEditStdOpen(false); toast.success("Standards updated — click Compile Manual to apply.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeScope = async (scope) => {
    try {
      const { data } = await api.put(`/policy-manuals/${selId}`, {
        title: detail.title, family: detail.family, description: detail.description || "",
        cip_senior_manager: detail.cip_senior_manager || "", approver_title: detail.approver_title || "",
        review_interval_months: detail.review_interval_months || 15,
        as_of_date: detail.as_of_date, standard_ids: detail.standard_ids || [],
        include_future_transition_language: detail.include_future_transition_language !== false,
        effective_scope: scope, policy_ids: detail.policy_ids || [],
      });
      setDetail(data); toast.success("Scope updated — click Compile Manual to apply.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const compile = async () => {
    setBusy("compile");
    try {
      const { data } = await api.post(`/policy-manuals/${selId}/compile`);
      setDetail(data);
      toast.success(`Compiled ${data.standard_count || 0} standards · ${data.statement_count || 0} policy statements`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const refreshWording = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post(`/policy-manuals/${selId}/refresh-wording`);
      setDetail(data);
      toast.success(`Wording refreshed · ${data.preserved_edits || 0} hand-edit(s) preserved`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const refreshAllWording = async () => {
    setBusy("refresh-all");
    try {
      const { data } = await api.post(`/policy-manuals/refresh-wording-all`);
      const kept = (data.manuals || []).reduce((a, m) => a + (m.preserved_edits || 0), 0);
      toast.success(`Refreshed ${data.refreshed} manual(s) · ${kept} hand-edit(s) preserved`);
      loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const exportManual = async (fmt) => {
    setBusy(`export-${fmt}`);
    try {
      const res = await api.get(`/policy-manuals/${selId}/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(detail.title || "policy_manual").replace(/[^A-Za-z0-9]+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed — compile the manual first."); }
    finally { setBusy(""); }
  };

  const exportGlossary = async () => {
    setBusy("export-glossary");
    try {
      const res = await api.get(`/policy-manuals/${selId}/glossary?format=pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(detail.title || "policy_manual").replace(/[^A-Za-z0-9]+/g, "_")}_acronyms.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported acronym cheat sheet");
    } catch (e) { toast.error("Export failed — compile the manual first."); }
    finally { setBusy(""); }
  };

  const signerTitle = (detail?.approver_title || "").trim() || ((detail?.family || "").trim().toUpperCase() === "CIP" ? "CIP Senior Manager" : "Authorized Approver");
  const isCipFam = (detail?.family || "").trim().toUpperCase() === "CIP";
  const openApprove = () => { setApproval({ approved_by: detail.cip_senior_manager || "", comment: "" }); setApproveOpen(true); };
  const approve = async () => {
    if (!approval.approved_by.trim()) return toast.error(`${signerTitle} name required`);
    try {
      const { data } = await api.post(`/policy-manuals/${selId}/approve`, approval);
      setDetail(data); setApproveOpen(false); loadList();
      toast.success(`Approved — next review due ${data.next_review_due}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeManual = async (id) => {
    if (!window.confirm("Delete this manual? (policies are not deleted)")) return;
    await api.delete(`/policy-manuals/${id}`); setSelId(null); loadList();
  };

  // ---------- LIST ----------
  if (!selId) {
    return (
      <div>
        <PageHeader title="Policy Manuals" subtitle="Compile family policies (CIP or O&P) for single-approver sign-off on a review cycle"
          action={
            <div className="flex items-center gap-3">
              <FamilyToggle testid="policy-manuals-family-toggle" />
              {canEdit && manuals.some((m) => m.compiled_at) && <Button data-testid="refresh-wording-all-btn" onClick={refreshAllWording} disabled={busy} variant="outline" className="rounded-none" title="Recompile every manual into the entity-voice wording, keeping your hand edits"><ArrowsClockwise size={16} className="mr-1" />{busy === "refresh-all" ? "Refreshing…" : "Refresh wording (all)"}</Button>}
              {canEdit && <Button data-testid="new-manual-btn" onClick={() => setCreating(true)} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Manual</Button>}
            </div>
          } />
        <div className="p-8">
          {manuals.length === 0 ? <Empty title="No policy manuals yet" hint="Create a CIP Policy Manual to compile all CIP policies under one approval." /> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {manuals.map((m) => (
                <button key={m.id} data-testid={`manual-card-${m.id}`} onClick={() => setSelId(m.id)} className="text-left border border-border bg-card p-5 hover:bg-secondary/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{m.family}</span><span className="font-display font-bold text-lg">{m.title}</span></div>
                    <ReviewBadge state={m.review_status?.state} days={m.review_status?.days_remaining} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
                    <div><span className="overline text-muted-foreground">Policies</span><div className="font-mono font-bold">{m.policy_count}</div></div>
                    <div><span className="overline text-muted-foreground">{signerFor(m)}</span><div className="truncate">{m.cip_senior_manager || "—"}</div></div>
                    <div><span className="overline text-muted-foreground">Next review</span><div className="font-mono">{m.next_review_due || "—"}</div></div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <CreateDialog open={creating} setOpen={setCreating} form={form} setForm={setForm} onCreate={create} />
      </div>
    );
  }

  // ---------- DETAIL ----------
  if (!detail) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const rs = detail.review_status || {};

  return (
    <div>
      <div className="border-b border-border px-8 py-5 bg-card sticky top-0 z-10 print:hidden">
        <button onClick={() => setSelId(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"><ArrowLeft size={14} /> Policy Manuals</button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{detail.family}</span>
            <h1 className="font-display font-black text-2xl tracking-tight">{detail.title}</h1>
            <ReviewBadge state={rs.state} days={rs.days_remaining} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && !editMode && <Button data-testid="edit-draft-btn" onClick={startEdit} disabled={(detail.compiled_body || []).length === 0} variant="outline" className="rounded-none"><Pencil size={16} className="mr-1" />Edit draft</Button>}
            {canEdit && editMode && <Button data-testid="save-draft-btn" onClick={saveDraft} disabled={busy === "save-draft"} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white">{busy === "save-draft" ? "Saving…" : "Save draft"}</Button>}
            {canEdit && editMode && <Button data-testid="cancel-edit-btn" onClick={cancelEdit} variant="outline" className="rounded-none">Cancel</Button>}
            {canEdit && !editMode && <Button data-testid="save-version-btn" onClick={saveVersion} disabled={(detail.compiled_body || []).length === 0} variant="outline" className="rounded-none"><FloppyDisk size={16} className="mr-1" />Save version</Button>}
            {!editMode && <Button data-testid="versions-btn" onClick={() => setShowVersions((v) => !v)} variant="outline" className="rounded-none"><ClockCounterClockwise size={16} className="mr-1" />Versions ({versions.length})</Button>}
            {canEdit && !editMode && <Button data-testid="compile-btn" onClick={compile} disabled={busy} variant="outline" className="rounded-none"><Stack size={16} className="mr-1" />{busy === "compile" ? "Compiling…" : "Compile Manual"}</Button>}
            {canEdit && !editMode && (detail.compiled_body || []).length > 0 && <Button data-testid="refresh-wording-btn" onClick={refreshWording} disabled={busy} variant="outline" className="rounded-none" title="Rebuild statements in the entity-voice wording; hand-edited text and front matter are kept"><ArrowsClockwise size={16} className="mr-1" />{busy === "refresh" ? "Refreshing…" : "Refresh wording"}</Button>}
            {!editMode && <Button data-testid="export-pdf-btn" onClick={() => exportManual("pdf")} disabled={busy} variant="outline" className="rounded-none"><FilePdf size={16} className="mr-1" />{busy === "export-pdf" ? "…" : "PDF"}</Button>}
            {!editMode && <Button data-testid="export-docx-btn" onClick={() => exportManual("docx")} disabled={busy} variant="outline" className="rounded-none"><FileDoc size={16} className="mr-1" />{busy === "export-docx" ? "…" : "Word"}</Button>}
            {!editMode && <Button data-testid="export-glossary-btn" onClick={exportGlossary} disabled={busy} variant="outline" className="rounded-none"><BookOpen size={16} className="mr-1" />{busy === "export-glossary" ? "…" : "Acronyms"}</Button>}
            {!editMode && <Button onClick={() => window.print()} variant="outline" className="rounded-none"><Printer size={16} className="mr-1" />Print</Button>}
            {canApprove && !editMode && <Button data-testid="approve-manual-btn" onClick={openApprove} className="rounded-none bg-primary hover:bg-primary/90"><SealCheck size={16} className="mr-1" />Approve</Button>}
            {canEdit && !editMode && <button data-testid="delete-manual-btn" aria-label="Delete manual" onClick={() => removeManual(detail.id)} className="text-muted-foreground hover:text-red-600 ml-1"><Trash size={18} /></button>}
          </div>
        </div>
      </div>

      <div className="p-8 max-w-4xl space-y-6">
        {/* Review cadence */}
        <div className={`border p-5 ${rs.state === "overdue" ? "border-red-300 bg-red-50" : rs.state === "due_soon" ? "border-amber-300 bg-amber-50" : "border-border bg-card"}`} data-testid="review-cadence">
          <div className="flex items-center gap-2 font-display font-bold"><ClockCounterClockwise size={18} />{signerTitle} Review Cycle</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
            <div><span className="overline text-muted-foreground">Interval</span><div className="font-mono">{detail.review_interval_months} months</div></div>
            <div><span className="overline text-muted-foreground">{signerTitle}</span><div>{detail.cip_senior_manager || "—"}</div></div>
            <div><span className="overline text-muted-foreground">Last approved</span><div className="font-mono">{detail.last_approved_at ? detail.last_approved_at.slice(0, 10) : "Never"}</div></div>
            <div><span className="overline text-muted-foreground">Next review due</span><div className="font-mono font-bold">{detail.next_review_due || "—"}</div></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Approving the manual sets the next review {detail.review_interval_months} calendar months out and creates a review task for the {signerTitle}.</p>
          <div className="mt-3 pt-3 border-t border-border/60" data-testid="manual-scope-control">
            <span className="overline text-muted-foreground">Standards selection</span>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="text-sm">
                {(detail.standard_ids?.length || 0) > 0 ? `${detail.standard_ids.length} standards selected` : scopeLabel(detail.effective_scope)}
                {detail.as_of_date ? ` · as of ${detail.as_of_date}` : ""}
              </span>
              {canEdit && <Button size="sm" variant="outline" data-testid="edit-standards-btn" onClick={openEditStd} className="rounded-none h-7 text-xs">Edit standards &amp; date</Button>}
              <span className="text-xs text-muted-foreground">Recompile to apply.</span>
            </div>
          </div>

          <Dialog open={editStdOpen} onOpenChange={setEditStdOpen}>
            <DialogContent className="rounded-none max-w-2xl" data-testid="edit-standards-dialog">
              <DialogHeader><DialogTitle>Edit standards &amp; enforcement date</DialogTitle></DialogHeader>
              <StandardsSelector family={detail.family} asOf={stdForm.as_of_date} standardIds={stdForm.standard_ids}
                onChange={({ as_of_date, standard_ids }) => setStdForm((f) => ({ ...f, as_of_date, standard_ids }))}
                testidPrefix="edit-std" />
              <label className="flex items-start gap-2 text-sm mt-3 cursor-pointer border border-amber-200 bg-amber-50/50 p-2.5">
                <input type="checkbox" data-testid="edit-std-transition-toggle" className="mt-0.5"
                  checked={stdForm.include_future_transition_language !== false}
                  onChange={(e) => setStdForm((f) => ({ ...f, include_future_transition_language: e.target.checked }))} />
                <span>
                  <span className="font-semibold">Include future-standard transition language</span>
                  <span className="block text-xs text-muted-foreground">Adds language that the entity shall establish procedures before each future-enforceable Standard&apos;s enforcement date. Uncheck if this manual takes effect immediately.</span>
                </span>
              </label>
              <Button data-testid="save-standards-btn" onClick={saveStandards} className="rounded-none w-full mt-3">Save standards</Button>
            </DialogContent>
          </Dialog>
        </div>

        {/* Multi-signature approval workflow (CIP Senior Manager + chain) — ClaroShield only */}
        {approvalsEnabled && (
        <div className="print:hidden" data-testid="manual-approval-workflow">
          <ApprovalWorkflow
            doc={detail}
            entityId={detail.id}
            basePath="/policy-manuals"
            manageRoles={["admin", "analyst", "reviewer"]}
            entityLabel="manual"
            bumpsVersion={false}
            onChange={(d) => { setDetail(d); loadList(); }}
          />
        </div>
        )}

        {/* Compiled manual */}
        <div className="border border-border bg-card" data-testid="compiled-manual">
          <div className="border-b border-border px-5 py-4 print:border-b-2">
            <h2 className="font-display font-black text-xl">{detail.title}</h2>
            <p className="text-sm text-muted-foreground">{detail.description || `Compilation of ${detail.family} ${isCipFam ? "cyber security " : ""}policies for ${detail.cip_senior_manager ? "approval by " + detail.cip_senior_manager : signerTitle + " approval"}.`}</p>
            {detail.last_approved_at && <p className="text-xs font-mono mt-2">Approved {detail.last_approved_at.slice(0, 10)} by {detail.last_approved_by} · Next review due {detail.next_review_due}</p>}
          </div>

          {(detail.compiled_body || []).length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">Not compiled yet. Click <strong>Compile Manual</strong> to build the {detail.family} policy manual grouped by Standard.</div>
          ) : (() => {
            const view = editMode ? draft : detail;
            const fmv = view.front_matter || {};
            const bodyv = view.compiled_body || [];
            return (
            <>
              {editMode && (
                <div className="px-5 py-2 bg-indigo-600 text-white text-xs font-semibold flex items-center gap-2" data-testid="edit-mode-banner">
                  <Pencil size={13} /> Edit mode — take the pen and revise any text below, then Save draft. Save a version first if you want a restore point.
                </div>
              )}
              {(fmv.executive_summary || editMode) && (
                <FrontSection title="Executive Summary" testid="manual-exec-summary" id="sec-fm_summary">
                  <Ed edit={editMode} value={fmv.executive_summary} onChange={(v) => setFM("executive_summary", v)} rows={4} className="text-sm" />
                  {!editMode && <p className="text-sm text-foreground/90"><GlossaryText text={fmv.executive_summary} /></p>}
                </FrontSection>
              )}
              <FrontSection title="Table of Contents" testid="manual-toc">
                <ol className="text-sm space-y-1 list-decimal pl-5">
                  {[["Glossary of Terms", "sec-fm_glossary"], ["Purpose", "sec-fm_purpose"], ["Scope and Applicability", "sec-fm_scope"], ["Enforcement", "sec-fm_enforcement"]].map(([t, id]) => (
                    <li key={t}><button type="button" onClick={() => scrollToSec(id)} className="text-indigo-700 dark:text-indigo-300 hover:underline text-left">{t}</button></li>
                  ))}
                  {bodyv.map((s) => (
                    <li key={s.standard_id}>
                      <button type="button" data-testid={`toc-link-${s.standard_id}`} onClick={() => scrollToSec(secId(s.standard_id))} className="text-indigo-700 dark:text-indigo-300 hover:underline text-left">
                        <span className="font-mono font-semibold">{s.standard_id}</span>{s.title ? ` — ${s.title}` : ""}
                      </button>
                    </li>
                  ))}
                </ol>
              </FrontSection>
              {(fmv.glossary?.length > 0) && (
                <FrontSection title="Glossary of Terms" testid="manual-glossary" id="sec-fm_glossary">
                  <p className="text-xs text-muted-foreground mb-2">{fmv.glossary_note}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {fmv.glossary.map((t) => <span key={t} className="text-xs border border-border px-2 py-0.5 bg-secondary/40 font-mono">{t}</span>)}
                  </div>
                </FrontSection>
              )}
              {(fmv.purpose_statement || editMode) && (
                <FrontSection title="Purpose" testid="manual-purpose" id="sec-fm_purpose">
                  <Ed edit={editMode} value={fmv.purpose_statement} onChange={(v) => setFM("purpose_statement", v)} rows={3} className="text-sm" />
                  {!editMode && <p className="text-sm text-foreground/90"><GlossaryText text={fmv.purpose_statement} /></p>}
                </FrontSection>
              )}
              {(fmv.scope_applicability?.length > 0) && (
                <FrontSection title="Scope and Applicability" testid="manual-scope" id="sec-fm_scope">
                  <div className="space-y-2">{fmv.scope_applicability.map((p, i) => editMode
                    ? <Ed key={i} edit value={p} onChange={(v) => setFMArr("scope_applicability", i, v)} />
                    : <p key={`scope-${i}`} className="text-sm text-foreground/90"><GlossaryText text={p} /></p>)}</div>
                </FrontSection>
              )}
              {(fmv.enforcement?.length > 0) && (
                <FrontSection title="Enforcement" testid="manual-enforcement" id="sec-fm_enforcement">
                  <div className="space-y-2">{fmv.enforcement.map((p, i) => editMode
                    ? <Ed key={i} edit value={p} onChange={(v) => setFMArr("enforcement", i, v)} />
                    : <p key={i} className="text-sm text-foreground/90"><GlossaryText text={p} /></p>)}</div>
                </FrontSection>
              )}
              <div className="px-5 py-3 border-b-2 border-black bg-primary text-primary-foreground">
                <h3 className="font-display font-black">Policy Statements by Standard</h3>
              </div>
              <div className="divide-y-2 divide-border">
                {bodyv.map((s, si) => (
                  <section key={s.standard_id} id={secId(s.standard_id)} className="px-5 py-5 scroll-mt-24" data-testid={`manual-standard-${s.standard_id}`}>
                    <div className="border-l-4 border-black pl-3 mb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{si + 1}.</span>
                        <h3 className="font-display font-black text-lg">{s.standard_id}</h3>
                        {s.title && <span className="text-sm text-muted-foreground">— {s.title}</span>}
                        {s.status && <StatusBadge status={s.status} />}
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs font-mono">
                        <span><span className="text-muted-foreground">Effective: </span>{s.effective_date || "—"}</span>
                        <span><span className="text-muted-foreground">Retirement: </span>{s.retirement_date || "—"}</span>
                        {s.ferc_order && <span><span className="text-muted-foreground">FERC Order: </span>{s.ferc_order}</span>}
                        {s.impl_plan_link && <a href={s.impl_plan_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`manual-implplan-${s.standard_id}`}>Implementation Plan ↗</a>}
                      </div>
                      {(s.purpose || editMode) && (
                        <div className="text-sm text-foreground/80 mt-2"><span className="overline text-muted-foreground">Purpose</span><br />
                          <Ed edit={editMode} value={s.purpose} onChange={(v) => setStdF(si, "purpose", v)} />
                          {!editMode && <GlossaryText text={s.purpose} />}
                        </div>
                      )}
                      {s.transition_note && (
                        <div className="mt-2 text-sm border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-amber-900" data-testid={`manual-transition-${s.standard_id}`}>
                          <span className="text-[10px] uppercase font-bold tracking-wide text-amber-700">Future enforceable — transition</span>
                          <div>{s.transition_note}</div>
                        </div>
                      )}
                    </div>
                    {s.impact_inventory && (
                      <div className="mb-4 ml-3 border border-border bg-secondary/30 p-3" data-testid={`manual-inventory-${s.standard_id}`}>
                        <div className="overline text-muted-foreground">BES Cyber System Categorization Inventory (CIP-002)</div>
                        <p className="text-xs text-muted-foreground mt-1 mb-2">The Entity has reviewed all of its assets to identify and categorize BES Cyber Systems.</p>
                        <ul className="space-y-1 text-sm">
                          {s.impact_inventory.map((it) => (
                            <li key={it.level} className="flex gap-2">
                              <span className="font-semibold w-32 shrink-0">{it.level} Impact:</span>
                              {it.identified
                                ? <span className="text-green-700">Identified — inventory maintained and retained per CIP-002.</span>
                                : <span className="text-foreground/70">None identified — <strong>null list</strong> documented and retained for audit.</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="space-y-4 pl-3">
                      {s.requirements.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No requirements imported for this standard.</p>
                      ) : s.requirements.map((r, ri) => (
                        <div key={r.req_number} data-testid={`manual-req-${s.standard_id}-${r.req_number}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-display font-bold text-sm">{si + 1}.{r.req_number}</h4>
                            <span className={`border px-2 py-0.5 text-[10px] font-semibold uppercase ${r.applicable ? "bg-green-50 text-green-700 border-green-300" : "bg-red-50 text-red-700 border-red-300"}`}>{r.applicable ? "Applicable" : "Not Applicable"}</span>
                          </div>
                          {r.effective_date && (
                            <div className="text-[11px] font-mono text-muted-foreground mb-1" data-testid={`manual-req-eff-${s.standard_id}-${r.req_number}`}>
                              <span className="text-muted-foreground">Effective: </span>{r.effective_date}
                              {r.effective_source === "implementation_plan" && <span className="ml-1 text-amber-700 not-italic">(per Implementation Plan)</span>}
                              {r.effective_note ? ` — ${r.effective_note}` : ""}
                            </div>
                          )}
                          {r.text && !r.endnote && <p className="text-xs text-muted-foreground italic mb-2"><GlossaryText text={r.text} /></p>}
                          {r.applicable ? (
                            <div className="text-sm text-foreground/90 space-y-1.5">
                              <Ed edit={editMode} value={r.statement} onChange={(v) => setReqF(si, ri, "statement", v)} rows={3} />
                              {!editMode && <p><GlossaryText text={r.statement} />{r.endnote && <sup className="ml-0.5 text-[10px] text-muted-foreground" data-testid={`manual-endnote-ref-${s.standard_id}-${r.req_number}`}>[{r.endnote}]</sup>}</p>}
                              {r.parts.length > 0 && (
                                <ul className="space-y-1 mt-1">
                                  {r.parts.map((p, pi) => (
                                    <li key={p.label} className="flex gap-2">
                                      <span className="font-mono font-semibold text-xs shrink-0">{p.label}</span>
                                      {editMode ? <Ed edit value={p.statement} onChange={(v) => setPartF(si, ri, pi, v)} className="flex-1" /> : <span><GlossaryText text={p.statement} /></span>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ) : editMode ? (
                            <div><span className="text-xs text-red-700 font-semibold">Not Applicable reason:</span><Ed edit value={r.reason} onChange={(v) => setReqF(si, ri, "reason", v)} /></div>
                          ) : (
                            <p className="text-sm text-red-700/90"><strong>Not Applicable</strong> — <GlossaryText text={r.reason} />{r.endnote && <sup className="ml-0.5 text-[10px] text-muted-foreground">[{r.endnote}]</sup>}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
                {(() => {
                  const notes = bodyv.flatMap((s) => s.requirements.filter((r) => r.endnote && r.text).map((r) => ({ n: r.endnote, sid: s.standard_id, rn: r.req_number, text: r.text })));
                  if (!notes.length) return null;
                  return (
                    <section className="px-5 py-5 border-t border-border" data-testid="manual-endnotes">
                      <h3 className="font-display font-bold text-base mb-1">Endnotes — Source Requirement Text</h3>
                      <p className="text-xs text-muted-foreground mb-3">The policy statements above restate the following NERC Reliability Standard requirement text as this Entity's own commitments. The authoritative requirement language is reproduced here for reference.</p>
                      <ol className="space-y-1.5">
                        {notes.map((e) => (
                          <li key={e.n} className="text-xs text-muted-foreground flex gap-2" data-testid={`manual-endnote-${e.n}`}>
                            <span className="font-mono font-semibold shrink-0">[{e.n}] {e.sid} {e.rn}:</span>
                            <span className="italic"><GlossaryText text={e.text} /></span>
                          </li>
                        ))}
                      </ol>
                    </section>
                  );
                })()}
              </div>
              <div className="px-5 py-3 border-t border-border text-center text-xs text-muted-foreground" data-testid="manual-claro-footer">
                Powered by Claro Compliance, LLC
              </div>
            </>
            );
          })()}
        </div>

        {/* Version history */}
        {showVersions && (
          <div className="border border-border bg-card print:hidden" data-testid="versions-panel">
            <div className="border-b border-border px-5 py-3 font-display font-bold flex items-center justify-between">
              <span>Saved Versions</span>
              {canEdit && <Button size="sm" variant="outline" onClick={saveVersion} disabled={(detail.compiled_body || []).length === 0} className="rounded-none h-7 text-xs"><FloppyDisk size={13} className="mr-1" />Save current as version</Button>}
            </div>
            <div className="p-5 space-y-2 text-sm">
              {versions.length === 0 ? <p className="text-muted-foreground text-sm">No saved versions yet. Save a version before big edits so you can revert.</p> : versions.map((v) => (
                <div key={v.id} data-testid={`version-${v.id}`} className="flex items-center gap-3 border-b border-border pb-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold">{v.label}</span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">{(v.saved_at || "").slice(0, 16).replace("T", " ")} · {v.saved_by} · {v.standard_count} std / {v.statement_count} stmts</span>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`restore-version-${v.id}`} onClick={() => restoreVersion(v)} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 border border-indigo-300 px-2 py-1 hover:bg-indigo-50"><ArrowUUpLeft size={13} />Restore</button>
                      <button data-testid={`delete-version-${v.id}`} onClick={() => deleteVersion(v)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approval history */}
        {detail.approval_log?.length > 0 && (
          <div className="border border-border bg-card print:hidden" data-testid="approval-history">
            <div className="border-b border-border px-5 py-3 font-display font-bold">Approval History</div>
            <div className="p-5 space-y-2 text-sm">
              {detail.approval_log.slice().reverse().map((a, i) => (
                <div key={a.at ?? i} className="border-b border-border pb-2">
                  <span className="font-semibold">{a.approved_by}</span> <span className="text-muted-foreground">({a.role})</span> approved on {a.at?.slice(0, 10)}
                  <div className="text-xs text-muted-foreground">Next review due {a.next_review_due}{a.comment ? ` · ${a.comment}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{signerTitle} Approval</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Approving sets the next review {detail.review_interval_months} calendar months from today.</p>
            <div><Label>{signerTitle}</Label><Input data-testid="approve-by-input" value={approval.approved_by} onChange={(e) => setApproval({ ...approval, approved_by: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Comment</Label><Textarea value={approval.comment} onChange={(e) => setApproval({ ...approval, comment: e.target.value })} className="rounded-none mt-1" placeholder="e.g. Periodic policy review" /></div>
            <Button data-testid="confirm-approve-btn" onClick={approve} className="rounded-none w-full bg-primary hover:bg-primary/90">Approve Manual</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateDialog({ open, setOpen, form, setForm, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle>New Policy Manual</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="manual-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="CIP Cyber Security Policy Manual" className="rounded-none mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Standard Family</Label>
              <Input data-testid="manual-family-input" value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value.toUpperCase(), standard_ids: [] })} placeholder="CIP or O&P" className="rounded-none mt-1" />
              <div className="flex gap-1.5 mt-1">
                <button type="button" data-testid="manual-family-cip" onClick={() => setForm({ ...form, family: "CIP", standard_ids: [] })}
                  className={`text-[11px] font-semibold border px-2 py-1 rounded-none ${form.family === "CIP" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>CIP</button>
                <button type="button" data-testid="manual-family-op" onClick={() => setForm({ ...form, family: "O&P", standard_ids: [] })}
                  className={`text-[11px] font-semibold border px-2 py-1 rounded-none ${form.family === "O&P" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>O&amp;P (all non-CIP)</button>
              </div>
            </div>
            <div><Label>Review Interval (months)</Label><Input type="number" value={form.review_interval_months} onChange={(e) => setForm({ ...form, review_interval_months: parseInt(e.target.value) || 15 })} className="rounded-none mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{form.family === "CIP" ? "CIP Senior Manager" : "Approver name"}</Label><Input data-testid="manual-csm-input" value={form.cip_senior_manager} onChange={(e) => setForm({ ...form, cip_senior_manager: e.target.value })} placeholder={form.family === "CIP" ? "Name of approving CIP Senior Manager" : "Name of approving official"} className="rounded-none mt-1" /></div>
            {form.family !== "CIP" && (
              <div><Label>Approver title</Label><Input data-testid="manual-approver-title-input" value={form.approver_title || ""} onChange={(e) => setForm({ ...form, approver_title: e.target.value })} placeholder="e.g. VP Operations / Compliance Manager" className="rounded-none mt-1" /></div>
            )}
          </div>
          <div>
            <Label>Standards Selection</Label>
            <StandardsSelector family={form.family} asOf={form.as_of_date} standardIds={form.standard_ids}
              onChange={({ as_of_date, standard_ids }) => setForm({ ...form, as_of_date, standard_ids })}
              testidPrefix="create-std" />
            <label className="flex items-start gap-2 text-sm mt-3 cursor-pointer border border-amber-200 bg-amber-50/50 p-2.5">
              <input type="checkbox" data-testid="create-std-transition-toggle" className="mt-0.5"
                checked={form.include_future_transition_language !== false}
                onChange={(e) => setForm({ ...form, include_future_transition_language: e.target.checked })} />
              <span>
                <span className="font-semibold">Include future-standard transition language</span>
                <span className="block text-xs text-muted-foreground">Adds language that the entity shall establish procedures before each future-enforceable Standard&apos;s enforcement date. Uncheck if this manual takes effect immediately.</span>
              </span>
            </label>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
          <Button data-testid="create-manual-btn" onClick={onCreate} className="rounded-none w-full bg-primary hover:bg-primary/90">Create Manual</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
